<script lang="ts" setup>
import { SancareMultiselect } from '@sancare/typescript-commons'
import { MultiSelectItem } from '@sancare/typescript-commons/src/components/types'
import { Dayjs } from 'dayjs'
import _ from 'lodash'
import { computed, ComputedRef } from 'vue'

import StayFilters from '@/common/StayFilters.vue'
import { createMulticheckOptions } from '@/filters/utils'
import { statusLabels } from '@/misc/constants'
import { useStore } from '@/store'
import { ModeEnum } from '@/store/modules/settings/types'

const store = useStore()

const props = defineProps<{
  mode: string
}>()

const emit = defineEmits(['edit', 'set-parameter'])

const params = computed(() => store.state.stayList.stayListParams)
const earlyLimit: ComputedRef<Dayjs | null> = computed(() => store.state.settings.earlyLimit)
const lateLimit: ComputedRef<Dayjs | null> = computed(() => store.state.settings.lateLimit)
const medicalUnits: ComputedRef<MultiSelectItem[]> = computed(() => props.mode === ModeEnum.DIALYSIS
  ? store.getters.dialysisMedicalUnits
  : store.state.settings.medicalUnits)
const selectedMedicalUnits: ComputedRef<number[]> = computed(() => store.state.settings.selectedMedicalUnits || [])
const medicalUnitsRequest = computed(() => store.state.remoteLists.medicalUnitsRequest)
const multicheckStatus = computed(() => createMulticheckOptions(statusLabels))

function handleDateRangeChange({ earlyLimit, lateLimit }: { earlyLimit: Dayjs | null, lateLimit: Dayjs | null }) {
  store.commit('setEarlyLimit', earlyLimit)
  store.commit('setLateLimit', lateLimit)
  emit('edit')
  store.dispatch('refreshStayList', { resetStaySelection: true })
  store.dispatch('refreshStayListUrl', {})
}

function updateMulticheck(
  newMulticheckValues: (string | number)[],
  labels: Record<string | number, string>,
  paramName: string
) {
  if (newMulticheckValues === null || newMulticheckValues.length === 0) {
    const keys = Object.keys(labels)
    const shouldBeNumbers = params.value[paramName]?.every((val: string | number) => typeof val === 'number')
    newMulticheckValues = shouldBeNumbers
      ? keys.map((key) => Number(key))
      : keys
  }
  if (_.isEqual(params.value[paramName], newMulticheckValues)) {
    return
  }
  emit('set-parameter', paramName, newMulticheckValues)
}

function handleMedicalUnitsChange(medicalUnitList: number[]) {
  store.commit('setSelectedMedicalUnits', medicalUnitList)
  store.dispatch('refreshStayList', { resetStaySelection: false })
  store.dispatch('refreshStayListUrl', {})
}

function handleStatusChange(status: string[]) {
  updateMulticheck(status, statusLabels, 'showStatus')
}

function handleHospitalizationTypesChange(types: number[]) {
  store.commit('setHospitalizationTypes', types)
  store.dispatch('refreshStayList', { resetStaySelection: false })
  store.dispatch('refreshStayListUrl', {})
}
</script>

<template>
  <div class="row py-3 align-items-end">
    <h2 class="text-start">
      {{ $t('parameters') }}
    </h2>
  </div>
  <div class="row align-items-center">
    <template v-if="params.improveRecallMode === false">
      <stay-filters
        :early-limit="earlyLimit"
        :late-limit="lateLimit"
        :selected-medical-units="selectedMedicalUnits"
        :medical-units="medicalUnits"
        :medical-units-request="medicalUnitsRequest"
        :show-medical-units-filter="mode !== ModeEnum.AUTOMATION"
        @update:date-range="handleDateRangeChange"
        @update:selected-medical-units="handleMedicalUnitsChange"
        @update:hospitalization-types="handleHospitalizationTypesChange"
      >
        <template #additional-filters>
          <div class="col-12 col-lg-4 my-3 my-lg-0 text-lg-center">
            <sancare-multiselect
              :title="$t('global.status')"
              :selected-items="params.showStatus"
              :items="multicheckStatus"
              @update-selection="handleStatusChange"
            />
          </div>
        </template>
      </stay-filters>
    </template>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
